<template>
  <div class="slicer-dimensao">
    <div class="slicer-header d-flex align-items-center justify-content-between mb-1">
      <span class="slicer-titulo fw-semibold small text-uppercase">
        <i :class="icone" class="me-1"></i> {{ titulo }}
      </span>
      <span v-if="ativosCount > 0" class="badge slicer-badge-ativos">{{ ativosCount }}</span>
    </div>

    <div v-if="opcoes.length > maxVisiveis" class="mb-1">
      <input
        v-model="buscaInterna"
        type="text"
        class="form-control form-control-sm slicer-search"
        :placeholder="'Buscar ' + titulo.toLowerCase() + '...'"
      />
    </div>

    <div v-if="loading" class="slicer-shimmer">
      <div v-for="n in 3" :key="n" class="shimmer-line mb-1"></div>
    </div>

    <div v-else class="slicer-chips">
      <div
        v-for="opcao in opcoesFiltradas.slice(0, expandido ? opcoesFiltradas.length : maxVisiveis)"
        :key="opcao.id"
        class="slicer-chip d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded cursor-pointer"
        :class="estaSelecionado(opcao.id) ? 'chip-ativo' : 'chip-inativo'"
        @click="toggleChip(opcao.id)"
        :title="opcao.tooltip || opcao.rotulo"
      >
        <span class="chip-label small text-truncate">
          <i v-if="estaSelecionado(opcao.id)" class="bi bi-check-circle-fill me-1 chip-icon-active"></i>
          <i v-else class="bi bi-circle me-1 chip-icon-inactive"></i>
          {{ opcao.rotulo }}
        </span>
        <span class="chip-count badge ms-1 flex-shrink-0">{{ formatarNumero(opcao.quantidadeItens) }}</span>
      </div>

      <div v-if="opcoesFiltradas.length > maxVisiveis" class="text-center">
        <button class="btn btn-link btn-sm text-decoration-none p-0 slicer-expand-btn" @click="expandido = !expandido">
          <i :class="expandido ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          {{ expandido ? 'Ver menos' : `Ver +${opcoesFiltradas.length - maxVisiveis} mais` }}
        </button>
      </div>

      <div v-if="opcoesFiltradas.length === 0" class="text-center slicer-empty py-2">
        <i class="bi bi-inbox me-1"></i> Nenhuma opção disponível
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  titulo: { type: String, required: true },
  icone: { type: String, default: 'bi bi-tag' },
  opcoes: { type: Array, default: () => [] },
  idsSelecionados: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  maxVisiveis: { type: Number, default: 5 }
})

const emit = defineEmits(['update:idsSelecionados'])

const buscaInterna = ref('')
const expandido = ref(false)

const ativosCount = computed(() => props.idsSelecionados.length)

const opcoesFiltradas = computed(() => {
  if (!buscaInterna.value) return props.opcoes
  const termo = buscaInterna.value.toLowerCase()
  return props.opcoes.filter(o =>
    o.rotulo?.toLowerCase().includes(termo) ||
    o.complemento?.toLowerCase().includes(termo)
  )
})

watch(() => props.opcoes, () => { expandido.value = false })

function estaSelecionado (id) {
  return props.idsSelecionados.includes(id)
}

function toggleChip (id) {
  const idx = props.idsSelecionados.indexOf(id)
  let novos
  if (idx >= 0) {
    novos = [...props.idsSelecionados]
    novos.splice(idx, 1)
  } else {
    novos = [...props.idsSelecionados, id]
  }
  emit('update:idsSelecionados', novos)
}

function formatarNumero (val) {
  if (val == null) return '0'
  if (val >= 1000) return (val / 1000).toFixed(1).replace('.0', '') + 'k'
  return val.toString()
}
</script>

<style scoped>
.slicer-dimensao {
  --slicer-primary: #0d6efd;
  --slicer-primary-bg: #e7f1ff;
  --slicer-primary-border: #0d6efd;
  --slicer-inativo-bg: var(--bs-tertiary-bg);
  --slicer-inativo-border: var(--bs-border-color);
  --slicer-text: var(--bs-body-color);
  --slicer-text-muted: var(--bs-secondary-color);
  --slicer-chip-count-bg: var(--bs-tertiary-bg);
  --slicer-chip-count-color: var(--bs-body-color);
  font-size: 0.8125rem;
}

[data-bs-theme="dark"] .slicer-dimensao {
  --slicer-primary-bg: #0a2a4a;
  --slicer-primary-border: #0d6efd;
  --slicer-inativo-bg: #1a1d23;
  --slicer-inativo-border: #2d323b;
  --slicer-chip-count-bg: #2d323b;
}

.slicer-header {
  border-bottom: 1px solid var(--bs-border-color);
  padding-bottom: 4px;
}

.slicer-titulo {
  letter-spacing: 0.3px;
  color: var(--slicer-text-muted);
}

.slicer-badge-ativos {
  background: var(--slicer-primary);
  color: white;
  font-size: 0.65rem;
}

.slicer-search {
  border-color: var(--slicer-inativo-border);
  background: var(--slicer-inativo-bg);
  color: var(--slicer-text);
}

.slicer-search:focus {
  border-color: var(--slicer-primary);
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
}

.chip-ativo {
  background: var(--slicer-primary-bg);
  border: 1px solid var(--slicer-primary-border);
  transition: all 0.15s ease;
}

.chip-inativo {
  background: var(--slicer-inativo-bg);
  border: 1px solid var(--slicer-inativo-border);
  transition: all 0.15s ease;
}

.chip-inativo:hover {
  background: var(--bs-tertiary-bg);
  border-color: var(--bs-secondary-color);
}

.chip-label {
  max-width: 70%;
  color: var(--slicer-text);
}

.chip-icon-active { color: var(--slicer-primary); }
.chip-icon-inactive { color: var(--slicer-text-muted); }

.chip-count {
  font-size: 0.7rem;
  line-height: 1.2;
  background: var(--slicer-chip-count-bg);
  color: var(--slicer-chip-count-color);
}

.slicer-expand-btn {
  color: var(--slicer-primary);
  font-size: 0.75rem;
}

.slicer-empty {
  color: var(--slicer-text-muted);
  font-size: 0.8rem;
}

.shimmer-line {
  height: 28px;
  background: linear-gradient(90deg, var(--bs-tertiary-bg) 25%, var(--bs-secondary-bg) 50%, var(--bs-tertiary-bg) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
